import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';

// icons
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SecurityIcon from '@mui/icons-material/Security';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// project imports
import MainCard from 'components/MainCard';
import { useAuth, SYSTEM_MODULES, SIDEBAR_MENU_OPTIONS } from 'pages/AuthProvider';
import StaffUserDialog from './StaffUserDialog';
import apiClient from 'utils/apiClient';

const slugify = (text) =>
  String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const MENU_GROUPS = [
  {
    title: 'Navigation',
    items: SIDEBAR_MENU_OPTIONS.filter((m) => m.group === 'Navigation')
  },
  {
    title: 'Product Details',
    items: SIDEBAR_MENU_OPTIONS.filter((m) => m.group === 'Product Details')
  },
  {
    title: 'Payments & Settings',
    items: SIDEBAR_MENU_OPTIONS.filter((m) => m.group === 'Payments')
  }
];

export default function RoleManagement() {
  const { roles, refreshRoles, user, canAccess } = useAuth();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [search, setSearch] = useState('');

  // Staff Users State
  const [staffUsers, setStaffUsers] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffDialogOpen, setStaffDialogOpen] = useState(false);
  const [preselectedRoleForUser, setPreselectedRoleForUser] = useState('');
  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deleteStaffConfirmOpen, setDeleteStaffConfirmOpen] = useState(false);
  const [deletingStaff, setDeletingStaff] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    accessibleMenus: [],
    permissions: {}
  });

  const fetchStaffUsers = useCallback(async () => {
    setStaffLoading(true);
    try {
      const res = await apiClient.get('/api/admin/staff');
      if (res?.data?.staff && Array.isArray(res.data.staff)) {
        setStaffUsers(res.data.staff);
      }
    } catch {
      // ignore
    } finally {
      setStaffLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaffUsers();
  }, [fetchStaffUsers]);

  const filteredRoles = useMemo(() => {
    if (!search.trim()) return roles;
    const q = search.toLowerCase();
    return roles.filter(
      (r) => r.name?.toLowerCase().includes(q) || r.key?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q)
    );
  }, [roles, search]);

  const handleOpenCreate = () => {
    setSelectedRole(null);
    setFormData({
      name: '',
      key: '',
      description: '',
      accessibleMenus: ['dashboard'],
      permissions: {
        dashboard: ['view']
      }
    });
    setErrorMsg('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (role) => {
    setSelectedRole(role);
    setFormData({
      name: role.name || '',
      key: role.key || '',
      description: role.description || '',
      accessibleMenus: Array.isArray(role.accessibleMenus) ? [...role.accessibleMenus] : [],
      permissions: role.permissions && typeof role.permissions === 'object' ? JSON.parse(JSON.stringify(role.permissions)) : {}
    });
    setErrorMsg('');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (saving) return;
    setDialogOpen(false);
    setSelectedRole(null);
    setErrorMsg('');
  };

  const handleToggleMenu = (menuId) => {
    setFormData((prev) => {
      const exists = prev.accessibleMenus.includes(menuId);
      const nextMenus = exists ? prev.accessibleMenus.filter((id) => id !== menuId) : [...prev.accessibleMenus, menuId];
      return { ...prev, accessibleMenus: nextMenus };
    });
  };

  const handleToggleMenuGroup = (groupItems) => {
    setFormData((prev) => {
      const groupIds = groupItems.map((i) => i.id);
      const allSelected = groupIds.every((id) => prev.accessibleMenus.includes(id));
      let nextMenus;
      if (allSelected) {
        nextMenus = prev.accessibleMenus.filter((id) => !groupIds.includes(id));
      } else {
        nextMenus = Array.from(new Set([...prev.accessibleMenus, ...groupIds]));
      }
      return { ...prev, accessibleMenus: nextMenus };
    });
  };

  const handleSelectAllMenus = (select) => {
    setFormData((prev) => ({
      ...prev,
      accessibleMenus: select ? SIDEBAR_MENU_OPTIONS.map((m) => m.id) : []
    }));
  };

  const handleTogglePermission = (moduleId, action) => {
    setFormData((prev) => {
      const currentActions = prev.permissions[moduleId] || [];
      const hasAction = currentActions.includes(action);
      const nextActions = hasAction ? currentActions.filter((a) => a !== action) : [...currentActions, action];

      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [moduleId]: nextActions
        }
      };
    });
  };

  const handleToggleModuleAll = (moduleItem) => {
    setFormData((prev) => {
      const currentActions = prev.permissions[moduleItem.id] || [];
      const allSelected = moduleItem.actions.every((a) => currentActions.includes(a));
      return {
        ...prev,
        permissions: {
          ...prev.permissions,
          [moduleItem.id]: allSelected ? [] : [...moduleItem.actions]
        }
      };
    });
  };

  const handleToggleColumnAction = (action) => {
    setFormData((prev) => {
      const modulesSupportingAction = SYSTEM_MODULES.filter((m) => m.actions.includes(action));
      const allHaveIt = modulesSupportingAction.every((m) => (prev.permissions[m.id] || []).includes(action));

      const nextPermissions = { ...prev.permissions };
      modulesSupportingAction.forEach((m) => {
        const current = nextPermissions[m.id] || [];
        if (allHaveIt) {
          nextPermissions[m.id] = current.filter((a) => a !== action);
        } else if (!current.includes(action)) {
          nextPermissions[m.id] = [...current, action];
        }
      });

      return { ...prev, permissions: nextPermissions };
    });
  };

  const handleGrantAllPermissions = () => {
    const full = {};
    SYSTEM_MODULES.forEach((m) => {
      full[m.id] = [...m.actions];
    });
    setFormData((prev) => ({
      ...prev,
      accessibleMenus: SIDEBAR_MENU_OPTIONS.map((m) => m.id),
      permissions: full
    }));
  };

  const handleSaveRole = async () => {
    if (!formData.name.trim()) {
      setErrorMsg('Role name is required');
      return;
    }

    const key = slugify(formData.key || formData.name);
    if (!key) {
      setErrorMsg('Valid role key is required');
      return;
    }

    setSaving(true);
    setErrorMsg('');

    try {
      const payload = {
        name: formData.name.trim(),
        key,
        description: formData.description.trim(),
        accessibleMenus: formData.accessibleMenus,
        permissions: formData.permissions
      };

      if (selectedRole?._id && !selectedRole._id.startsWith('preset-')) {
        await apiClient.put(`/api/admin/roles/${selectedRole._id}`, payload);
        setToast({ open: true, message: `Role "${formData.name}" updated successfully.`, severity: 'success' });
      } else {
        await apiClient.post('/api/admin/roles', payload);
        setToast({ open: true, message: `Role "${formData.name}" created successfully.`, severity: 'success' });
      }

      await refreshRoles();
      setDialogOpen(false);
      setSelectedRole(null);
    } catch (err) {
      setErrorMsg(err.response?.data?.message || err.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async () => {
    if (!roleToDelete) return;
    setDeleting(true);
    try {
      if (roleToDelete._id && !roleToDelete._id.startsWith('preset-')) {
        await apiClient.delete(`/api/admin/roles/${roleToDelete._id}`);
      }
      setToast({ open: true, message: `Role "${roleToDelete.name}" deleted.`, severity: 'info' });
      await refreshRoles();
      setDeleteConfirmOpen(false);
      setRoleToDelete(null);
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.message || 'Failed to delete role', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteStaffUser = async () => {
    if (!staffToDelete) return;
    setDeletingStaff(true);
    try {
      await apiClient.delete(`/api/admin/staff/${staffToDelete._id}`);
      setToast({ open: true, message: `Staff user "${staffToDelete.name}" removed.`, severity: 'info' });
      await fetchStaffUsers();
      setDeleteStaffConfirmOpen(false);
      setStaffToDelete(null);
    } catch (err) {
      setToast({ open: true, message: err.response?.data?.message || 'Failed to delete staff user', severity: 'error' });
    } finally {
      setDeletingStaff(false);
    }
  };

  const handleCopyEmail = (email) => {
    navigator.clipboard.writeText(email);
    setToast({ open: true, message: `Copied "${email}" to clipboard`, severity: 'success' });
  };

  return (
    <Stack spacing={3}>
      {/* Page Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SecurityIcon color="primary" fontSize="medium" /> Roles & Permissions
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Configure custom roles, control sidebar menus, and generate login emails & passwords for role users.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          {canAccess('users', 'create') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => {
                setPreselectedRoleForUser('');
                setStaffDialogOpen(true);
              }}
            >
              Generate Role User
            </Button>
          )}
          {canAccess('roles', 'create') && (
            <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleOpenCreate}>
              Create New Role
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Roles Summary Table */}
      <MainCard
        title={`Configured Roles (${filteredRoles.length})`}
        secondary={
          <TextField
            size="small"
            placeholder="Search roles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: '100%', sm: 220 } }}
          />
        }
      >
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Role Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role Key</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sidebar Access</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Action Permissions</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRoles.map((role) => {
                const isSystem = Boolean(role.isSystem);
                const accessibleCount = Array.isArray(role.accessibleMenus) ? role.accessibleMenus.length : 0;
                const permissionsCount = Object.keys(role.permissions || {}).length;

                return (
                  <TableRow hover key={role._id || role.key}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {role.name}
                        </Typography>
                        {isSystem && (
                          <Tooltip title="System Role: Core built-in role">
                            <LockOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={role.key} variant="outlined" sx={{ fontFamily: 'monospace' }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={isSystem ? 'System' : 'Custom'}
                        color={isSystem ? 'primary' : 'secondary'}
                        variant={isSystem ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" color="text.secondary" noWrap title={role.description}>
                        {role.description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`${accessibleCount} / ${SIDEBAR_MENU_OPTIONS.length} menus`}
                        color={accessibleCount === SIDEBAR_MENU_OPTIONS.length ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {permissionsCount} modules configured
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {canAccess('users', 'create') && (
                          <Tooltip title={`Generate user login for ${role.name}`}>
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => {
                                setPreselectedRoleForUser(role.key);
                                setStaffDialogOpen(true);
                              }}
                            >
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Permissions">
                          <IconButton size="small" color="primary" onClick={() => handleOpenEdit(role)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {!isSystem && (
                          <Tooltip title="Delete Role">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setRoleToDelete(role);
                                setDeleteConfirmOpen(true);
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!filteredRoles.length && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No roles found matching &quot;{search}&quot;.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </MainCard>

      {/* Staff & Role User Accounts Card */}
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleAltOutlinedIcon color="primary" />
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Staff & Role User Accounts ({staffUsers.length})
            </Typography>
          </Stack>
        }
        secondary={
          <Button
            size="small"
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setPreselectedRoleForUser('');
              setStaffDialogOpen(true);
            }}
          >
            Generate New Login
          </Button>
        }
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These are admin panel accounts with assigned roles. Users can log in using their email and generated password at /login.
        </Typography>

        {staffLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Login Email</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Assigned Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {staffUsers.map((st) => (
                  <TableRow hover key={st._id}>
                    <TableCell sx={{ fontWeight: 600 }}>{st.name}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="body2">{st.email}</Typography>
                        <Tooltip title="Copy Email">
                          <IconButton size="small" onClick={() => handleCopyEmail(st.email)}>
                            <ContentCopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={st.role || 'admin'} color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={st.isActive ? 'Active' : 'Inactive'}
                        color={st.isActive ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{st.phone || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {st.createdAt ? new Date(st.createdAt).toLocaleDateString() : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove Staff User">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setStaffToDelete(st);
                            setDeleteStaffConfirmOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {!staffUsers.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No staff accounts generated yet. Click &quot;Generate New Login&quot; to create one.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </MainCard>

      {/* Role Editor Modal */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {selectedRole ? `Edit Role: ${selectedRole.name}` : 'Create New Role'}
            </Typography>
            <Button size="small" variant="outlined" color="primary" onClick={handleGrantAllPermissions}>
              Grant Full Access
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          {errorMsg && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMsg}
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Section 1: Basic Info */}
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                  1. Role Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Role Name"
                      placeholder="e.g. Operations Manager"
                      value={formData.name}
                      onChange={(e) => {
                        const name = e.target.value;
                        setFormData((prev) => ({
                          ...prev,
                          name,
                          key: prev.key && selectedRole ? prev.key : slugify(name)
                        }));
                      }}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Role Key / Identifier"
                      placeholder="e.g. operations-manager"
                      value={formData.key}
                      onChange={(e) => setFormData((prev) => ({ ...prev, key: slugify(e.target.value) }))}
                      disabled={Boolean(selectedRole?.isSystem)}
                      helperText={selectedRole?.isSystem ? 'System role key is locked' : 'Normalized identifier'}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Description"
                      placeholder="Brief description of what this role does"
                      value={formData.description}
                      onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                      multiline
                      rows={2}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Section 2: Sidebar Menu Visibility */}
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      2. Sidebar Menu Visibility
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose which navigation menu options will appear in the admin drawer for this role.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={() => handleSelectAllMenus(true)}>
                      Select All
                    </Button>
                    <Button size="small" color="inherit" onClick={() => handleSelectAllMenus(false)}>
                      Deselect All
                    </Button>
                  </Stack>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  {MENU_GROUPS.map((group) => (
                    <Grid size={{ xs: 12, md: 4 }} key={group.title}>
                      <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'grey.50' }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            {group.title}
                          </Typography>
                          <Button size="small" sx={{ fontSize: '0.7rem', py: 0 }} onClick={() => handleToggleMenuGroup(group.items)}>
                            Toggle
                          </Button>
                        </Stack>
                        <Stack spacing={0.5}>
                          {group.items.map((menuItem) => {
                            const isChecked = formData.accessibleMenus.includes(menuItem.id);
                            return (
                              <FormControlLabel
                                key={menuItem.id}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={isChecked}
                                    onChange={() => handleToggleMenu(menuItem.id)}
                                  />
                                }
                                label={<Typography variant="body2">{menuItem.title}</Typography>}
                                sx={{ ml: 0 }}
                              />
                            );
                          })}
                        </Stack>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* Section 3: Granular Action Permissions Matrix */}
            <Card variant="outlined">
              <CardContent sx={{ pb: '16px !important' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      3. Granular Action Permissions
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Manage View, Create, Edit, and Delete action permissions for each feature area.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5}>
                    <Button size="small" onClick={() => handleToggleColumnAction('view')}>
                      All View
                    </Button>
                    <Button size="small" onClick={() => handleToggleColumnAction('create')}>
                      All Create
                    </Button>
                    <Button size="small" onClick={() => handleToggleColumnAction('edit')}>
                      All Edit
                    </Button>
                    <Button size="small" color="error" onClick={() => handleToggleColumnAction('delete')}>
                      All Delete
                    </Button>
                  </Stack>
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, minWidth: 160 }}>Module / Feature</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>View</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Create</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Edit</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>Delete</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Toggle</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {SYSTEM_MODULES.map((moduleItem) => {
                        const modulePerms = formData.permissions[moduleItem.id] || [];
                        const canView = modulePerms.includes('view');
                        const canCreate = modulePerms.includes('create');
                        const canEdit = modulePerms.includes('edit');
                        const canDelete = modulePerms.includes('delete');

                        const supportsCreate = moduleItem.actions.includes('create');
                        const supportsEdit = moduleItem.actions.includes('edit');
                        const supportsDelete = moduleItem.actions.includes('delete');

                        return (
                          <TableRow hover key={moduleItem.id}>
                            <TableCell>
                              <Typography variant="subtitle2">{moduleItem.label}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {moduleItem.id}
                              </Typography>
                            </TableCell>

                            <TableCell align="center">
                              <Checkbox
                                size="small"
                                color="primary"
                                checked={canView}
                                onChange={() => handleTogglePermission(moduleItem.id, 'view')}
                              />
                            </TableCell>

                            <TableCell align="center">
                              {supportsCreate ? (
                                <Checkbox
                                  size="small"
                                  color="success"
                                  checked={canCreate}
                                  onChange={() => handleTogglePermission(moduleItem.id, 'create')}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="center">
                              {supportsEdit ? (
                                <Checkbox
                                  size="small"
                                  color="warning"
                                  checked={canEdit}
                                  onChange={() => handleTogglePermission(moduleItem.id, 'edit')}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="center">
                              {supportsDelete ? (
                                <Checkbox
                                  size="small"
                                  color="error"
                                  checked={canDelete}
                                  onChange={() => handleTogglePermission(moduleItem.id, 'delete')}
                                />
                              ) : (
                                <Typography variant="caption" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                size="small"
                                sx={{ fontSize: '0.7rem', py: 0.2 }}
                                onClick={() => handleToggleModuleAll(moduleItem)}
                              >
                                {moduleItem.actions.every((a) => modulePerms.includes(a)) ? 'Clear' : 'All'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveRole} variant="contained" color="primary" disabled={saving}>
            {saving ? 'Saving...' : selectedRole ? 'Save Changes' : 'Create Role'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Staff User Generation Modal */}
      <StaffUserDialog
        open={staffDialogOpen}
        onClose={() => setStaffDialogOpen(false)}
        roles={roles}
        preselectedRole={preselectedRoleForUser}
        onSuccess={(newUser) => {
          setToast({
            open: true,
            message: `Account generated for ${newUser.name} with role "${newUser.role}".`,
            severity: 'success'
          });
          fetchStaffUsers();
        }}
      />

      {/* Delete Role Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Role?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete the role <strong>{roleToDelete?.name}</strong>? Users assigned to this role may lose their permissions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteRole} variant="contained" color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Staff User Confirmation Modal */}
      <Dialog open={deleteStaffConfirmOpen} onClose={() => setDeleteStaffConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Remove Staff Account?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to remove the login account for <strong>{staffToDelete?.name}</strong> ({staffToDelete?.email})? They will no longer be able to log in to the admin panel.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={() => setDeleteStaffConfirmOpen(false)} color="inherit" disabled={deletingStaff}>
            Cancel
          </Button>
          <Button onClick={handleDeleteStaffUser} variant="contained" color="error" disabled={deletingStaff}>
            {deletingStaff ? 'Removing...' : 'Remove Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
